import { ComponentType } from "react";
import { Link } from "react-router";
import {
  ArrowRight,
  Calendar,
  CalendarDays,
  Clock,
  MapPin,
  Repeat,
  UserRound,
} from "lucide-react";
import { format } from "date-fns";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { AppointmentDto } from "../../api/appointmentApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import {
  getInstrumentIcon,
  getTranslations,
  useSupportedInstruments,
} from "../../api/instrumentApi.ts";
import { formatSmartDate } from "./formatBooking.ts";
import Modal from "../util/Modal.tsx";

interface AppointmentDetailsModalProps {
  appointment: AppointmentDto | null;
  onClose: () => void;
}

function DetailRow({
  icon: Icon,
  children,
}: {
  icon: ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="text-soluud-primary h-4 w-4 shrink-0" />
      <span className="text-soluud-text-primary text-sm">{children}</span>
    </div>
  );
}

function AppointmentDetails({
  appointment,
  onClose,
}: {
  appointment: AppointmentDto;
  onClose: () => void;
}) {
  const { language, dateFnsLocale } = useLanguageStore();
  const { data: supportedInstruments } = useSupportedInstruments();

  const InstrumentIcon = getInstrumentIcon(appointment.instrument);
  const instrumentLabel = getTranslations(
    supportedInstruments,
    appointment.instrument,
    language,
  );

  const fromDate = new Date(appointment.from);
  const toDate = new Date(appointment.to);
  const dateLabel = formatSmartDate(
    fromDate,
    dateFnsLocale,
    t`Today`,
    t`Tomorrow`,
  );
  const timeRange = `${format(fromDate, "HH:mm", { locale: dateFnsLocale })} – ${format(toDate, "HH:mm", { locale: dateFnsLocale })}`;
  const isSubscription = appointment.source === "SUBSCRIPTION";

  return (
    <div className="bg-soluud-background-card border-soluud-border-subtle rounded-xl border p-6 shadow-[--shadow-card]">
      <div className="mb-5 flex items-start justify-between gap-3 pr-8">
        <h2
          id="appointment-details-title"
          className="text-soluud-text-primary text-lg font-semibold"
        >
          <Trans>Lesson details</Trans>
        </h2>
        {isSubscription ? (
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-purple-100 px-2.5 py-0.5 text-[11px] font-medium text-purple-700">
            <Repeat className="h-3 w-3" />
            <Trans>Subscription</Trans>
          </span>
        ) : (
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-[11px] font-medium text-blue-700">
            <Calendar className="h-3 w-3" />
            <Trans>Single</Trans>
          </span>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <DetailRow icon={CalendarDays}>{dateLabel}</DetailRow>
        <DetailRow icon={Clock}>{timeRange}</DetailRow>
        <DetailRow icon={InstrumentIcon}>{instrumentLabel}</DetailRow>
        <DetailRow icon={UserRound}>{appointment.personName}</DetailRow>
        <DetailRow icon={MapPin}>{appointment.locationName}</DetailRow>
      </div>

      <Link
        to={`/profile/${appointment.personId}`}
        onClick={onClose}
        className="text-soluud-primary hover:text-soluud-primary/80 mt-6 inline-flex items-center gap-1.5 text-sm font-medium transition-colors"
      >
        <Trans>View profile</Trans>
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

export default function AppointmentDetailsModal({
  appointment,
  onClose,
}: AppointmentDetailsModalProps) {
  return (
    <Modal
      show={appointment != null}
      onClose={onClose}
      className="w-full max-w-lg"
      ariaLabelledBy="appointment-details-title"
    >
      {appointment && (
        <AppointmentDetails appointment={appointment} onClose={onClose} />
      )}
    </Modal>
  );
}
